import { Link, useOutletContext } from '@remix-run/react';
import { BadgePercent, Loader2Icon, Plus, ShoppingCart } from 'lucide-react';
import { useState } from 'react';
import { SearchQuery } from '~/generated/graphql';
import { OutletContext } from '~/types';
import { calculateEffectivePrice } from '~/utils/pricing';
import { Price } from './Price';

export type ProductListCardProps = SearchQuery['search']['items'][number];

export function ProductListCard({
  productAsset,
  productName,
  slug,
  priceWithTax,
  currencyCode,
  description,
  productVariantId,
  productVariant,
  inStock,
}: ProductListCardProps) {
  const { activeOrderFetcher: fetcher, merchantContextRequired } =
    useOutletContext<OutletContext>();
  const [quantity, setQuantity] = useState<number>(1);

  // Check if item is in cart
  const { activeOrder } = fetcher.data ?? {};
  const isItemInCart =
    activeOrder?.lines.some(
      (line) => line.productVariant.id === productVariantId,
    ) ?? false;

  const basePrice =
    typeof priceWithTax === 'object' && 'value' in priceWithTax
      ? priceWithTax.value
      : typeof priceWithTax === 'object' && 'min' in priceWithTax
      ? priceWithTax.min
      : 0;

  const priceTiers = productVariant?.customFields?.priceTiers || [];
  const hasPriceTiers = Array.isArray(priceTiers) && priceTiers.length > 0;

  const { effectiveUnitPrice } = calculateEffectivePrice(
    basePrice || 0,
    priceTiers,
    quantity,
  );

  const price = effectiveUnitPrice;

  const customOriginalPrice = productVariant?.customFields?.originalPrice;
  const originalPrice =
    customOriginalPrice && customOriginalPrice > price
      ? customOriginalPrice
      : basePrice && basePrice > price
      ? basePrice
      : undefined;

  const isBeingAddedToCart =
    fetcher.state === 'submitting' &&
    fetcher.formData?.get('variantId') === productVariantId;

  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleAddToCart = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!fetcher || !productVariantId || !inStock || merchantContextRequired)
      return;

    fetcher.submit(
      {
        action: 'addItemToOrder',
        variantId: productVariantId,
        quantity: quantity.toString(),
      },
      {
        method: 'post',
        action: '/api/active-order',
      },
    );
  };

  return (
    <div className="flex w-full rounded-xl overflow-hidden hover:shadow-md transition-shadow">
      {/* Image Container - Left Side - Smaller */}
      <Link
        to={`/products/${slug}`}
        prefetch="intent"
        className="relative w-24 sm:w-28 aspect-square flex-shrink-0"
      >
        {(originalPrice ?? 0) > price && (
          <div className="absolute top-0 left-0 bg-[#E80245] text-white text-[10px] font-bold px-1.5 py-0.5 z-10 rounded-br-lg">
            Sale
          </div>
        )}
        {productAsset?.preview ? (
          <img
            src={`${productAsset.preview}?w=200&h=200&format=webp`}
            alt={productName}
            className={`w-full h-full object-contain p-2 mix-blend-multiply ${
              !inStock ? 'grayscale opacity-70' : ''
            }`}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <img
              src="/brand_logo.png"
              alt="Logo placeholder"
              className="w-1/2 h-auto opacity-30"
            />
          </div>
        )}
      </Link>

      {/* Content Section - Right Side */}
      <div className="flex flex-col flex-1 p-3 justify-between">
        <div>
          <div className="flex justify-between items-start gap-2">
            {/* Title */}
            <Link
              to={`/products/${slug}`}
              prefetch="intent"
              className="block flex-1 min-w-0"
            >
              <h3 className="text-sm font-medium text-gray-900 line-clamp-2 hover:text-[#E80245] transition-colors">
                {productName}
              </h3>
            </Link>

            {/* Add to Cart Button - Top Right */}
            {inStock && (
              <button
                type="button"
                onClick={handleAddToCart}
                disabled={isBeingAddedToCart || merchantContextRequired}
                className="flex-shrink-0 w-8 h-8 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 hover:text-[#E80245] hover:border-[#E80245] active:scale-95 transition-all disabled:opacity-50"
              >
                {isBeingAddedToCart ? (
                  <Loader2Icon size={14} className="animate-spin" />
                ) : (
                  <Plus size={16} strokeWidth={2.5} />
                )}
              </button>
            )}
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-2 mt-1">
            <span className="text-base font-bold text-gray-900">
              <Price priceWithTax={price} currencyCode={currencyCode} />
            </span>
            {(originalPrice ?? 0) > price && (
              <div className="relative inline-block">
                <span className="text-gray-400 text-xs">
                  <Price
                    priceWithTax={originalPrice}
                    currencyCode={currencyCode}
                  />
                </span>
                <div className="absolute top-1/2 left-0 w-full h-[1px] bg-[#E80245] -translate-y-1/2 transform rotate-[-3deg]"></div>
              </div>
            )}
          </div>

          {/* Description */}
          <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">
            {description
              ? description.replace(/<[^>]*>/g, '').slice(0, 100)
              : ''}
          </p>
        </div>

        {/* Bulk Pricing - Bottom */}
        <div className="mt-2">
          <div className="flex flex-wrap gap-1">
            {hasPriceTiers &&
              priceTiers.slice(0, 2).map((tier, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1 text-[#E80245] text-[10px] font-medium bg-[#FFF0F3] px-1.5 py-0.5 rounded-full"
                >
                  <BadgePercent size={10} className="flex-shrink-0" />
                  <span>
                    <Price
                      priceWithTax={(tier.price ?? 0) * 100}
                      currencyCode={currencyCode}
                      variant="primary"
                    />
                    /{tier.minQty}+
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
